import React from 'react';
import { Link } from 'react-router-dom';
import { siteConfig } from '../assets/config';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy-page">
      {/* Hero Section */}
      <section className="privacy-hero">
        <div className="container">
          <div className="privacy-hero-content">
            <h1>Privacy Policy</h1>
            <p>How we collect, use, and protect your information</p>
          </div>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="section privacy-content">
        <div className="container">
          <div className="policy-content">
            <div className="last-updated">
              <p><strong>Last Updated:</strong> {new Date().toLocaleDateString()}</p>
            </div>

            <div className="policy-section">
              <h2>1. Introduction</h2>
              <p>
                Musespace ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website at {siteConfig.domain || 'musespace.in'} and use our services.
              </p>
              <p>
                By using our website and services, you consent to the data practices described in this policy.
              </p>
            </div>

            <div className="policy-section">
              <h2>2. Information We Collect</h2>
              
              <h3>2.1 Personal Information</h3>
              <p>We may collect personal information that you voluntarily provide to us, including:</p>
              <ul>
                <li>Name and contact information (email, phone number)</li>
                <li>Project details and requirements</li>
                <li>Communication preferences</li>
                <li>Any other information you provide through our contact forms</li>
              </ul>

              <h3>2.2 Automatically Collected Information</h3>
              <p>When you visit our website, we may automatically collect:</p>
              <ul>
                <li>IP address and location data</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Pages visited and time spent</li>
                <li>Referring website</li>
                <li>Device information</li>
              </ul>

              <h3>2.3 Cookies and Tracking Technologies</h3>
              <p>We use cookies and similar technologies to:</p>
              <ul>
                <li>Improve website functionality</li>
                <li>Analyze website traffic</li>
                <li>Remember your preferences</li>
                <li>Provide personalized content</li>
              </ul>
            </div>

            <div className="policy-section">
              <h2>3. How We Use Your Information</h2>
              <p>We use the collected information for the following purposes:</p>
              <ul>
                <li>Provide and maintain our services</li>
                <li>Respond to your inquiries and requests</li>
                <li>Send you project updates and communications</li>
                <li>Improve our website and services</li>
                <li>Analyze usage patterns and trends</li>
                <li>Comply with legal obligations</li>
                <li>Protect against fraud and abuse</li>
              </ul>
            </div>

            <div className="policy-section">
              <h2>4. Information Sharing and Disclosure</h2>
              <p>We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:</p>
              <ul>
                <li><strong>Service Providers:</strong> With trusted third-party service providers who assist us in operating our website and providing services</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                <li><strong>Consent:</strong> With your explicit consent</li>
              </ul>
            </div>

            <div className="policy-section">
              <h2>5. Data Security</h2>
              <p>We implement appropriate security measures to protect your personal information, including:</p>
              <ul>
                <li>Encryption of sensitive data</li>
                <li>Secure servers and networks</li>
                <li>Regular security assessments</li>
                <li>Access controls and authentication</li>
                <li>Employee training on data protection</li>
              </ul>
            </div>

            <div className="policy-section">
              <h2>6. Your Rights and Choices</h2>
              <p>You have the following rights regarding your personal information:</p>
              <ul>
                <li><strong>Access:</strong> Request access to your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Cookie Preferences:</strong> Manage cookie settings in your browser</li>
              </ul>
            </div>

            <div className="policy-section">
              <h2>7. Third-Party Services</h2>
              <p>Our website may contain links to third-party websites and services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies.</p>
              
              <h3>7.1 Third-Party Services We Use</h3>
              <ul>
                <li><strong>EmailJS:</strong> For contact form functionality</li>
                <li><strong>Firebase:</strong> For website hosting and analytics</li>
                <li><strong>Google Analytics:</strong> For website analytics (if enabled)</li>
              </ul>
            </div>

            <div className="policy-section">
              <h2>8. Children's Privacy</h2>
              <p>Our website is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.</p>
            </div>

            <div className="policy-section">
              <h2>9. International Data Transfers</h2>
              <p>Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.</p>
            </div>

            <div className="policy-section">
              <h2>10. Changes to This Privacy Policy</h2>
              <p>We may update this Privacy Policy from time to time. We will notify you of any changes by:</p>
              <ul>
                <li>Posting the new Privacy Policy on this page</li>
                <li>Updating the "Last Updated" date</li>
                <li>Sending you an email notification (if applicable)</li>
              </ul>
              <p>Your continued use of our website after any changes constitutes acceptance of the updated Privacy Policy.</p>
            </div>

            <div className="policy-section">
              <h2>11. Contact Us</h2>
              <p>If you have any questions about this Privacy Policy or our data practices, please contact us:</p>
              <div className="contact-info">
                <p><strong>Email:</strong> <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a></p>
                <p><strong>Phone:</strong> <a href={`tel:${siteConfig.contact.phone}`}>{siteConfig.contact.phone}</a></p>
                <p><strong>Address:</strong> {siteConfig.contact.address}</p>
              </div>
            </div>

            <div className="policy-links">
              <Link to="/terms" className="btn btn-secondary">Terms of Service</Link>
              <Link to="/" className="btn btn-primary">Back to Home</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy; 